/**
 * Custom scrollbar for Visore Slideshow on mobile devices
 * Adds a draggable custom scrollbar that allows navigation between slides
 */
class VisoreSlideShowScrollbar extends HTMLElement {
  constructor() {
    super();
    this.slideshow = null;
    this.customThumb = null;
    this.isDraggingThumb = false;
  }

  connectedCallback() {
    // Only initialize on mobile
    if (window.innerWidth > 768) return;

    this.slideshow = this.querySelector('.slideshow');
    if (!this.slideshow) return;

    this.initCustomScrollbar();

    // Reinitialize on window resize
    this.resizeHandler = () => {
      if (window.innerWidth > 768) {
        this.destroyCustomScrollbar();
      } else if (!this.customThumb) {
        this.initCustomScrollbar();
      }
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    this.destroyCustomScrollbar();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * Initializes custom scrollbar for mobile devices
   */
  initCustomScrollbar() {
    if (this.customThumb || !this.slideshow) return;

    // Always create track element (Safari doesn't support pseudo-elements on custom elements)
    if (!this.querySelector('.visore-scrollbar-track')) {
      const track = document.createElement('div');
      track.className = 'visore-scrollbar-track';
      this.appendChild(track);
    }

    // Create custom scrollbar thumb element
    this.customThumb = document.createElement('div');
    this.customThumb.className = 'visore-scrollbar-thumb';
    this.appendChild(this.customThumb);

    // Update scrollbar on scroll
    this.customScrollHandler = this.updateCustomScrollbar.bind(this);
    this.slideshow.addEventListener('scroll', this.customScrollHandler);

    // Add drag functionality to custom scrollbar
    this.thumbMouseDownHandler = this.handleThumbMouseDown.bind(this);
    this.thumbTouchStartHandler = this.handleThumbTouchStart.bind(this);

    this.customThumb.addEventListener('mousedown', this.thumbMouseDownHandler);
    this.customThumb.addEventListener('touchstart', this.thumbTouchStartHandler, { passive: false });

    // Initial update
    this.updateCustomScrollbar();
  }

  /**
   * Destroys custom scrollbar
   */
  destroyCustomScrollbar() {
    if (this.customThumb) {
      this.customThumb.remove();
      this.customThumb = null;
    }
    if (this.customScrollHandler && this.slideshow) {
      this.slideshow.removeEventListener('scroll', this.customScrollHandler);
    }
  }

  /**
   * Updates the position and width of custom scrollbar thumb
   */
  updateCustomScrollbar() {
    if (!this.customThumb || !this.slideshow) return;

    const scrollWidth = this.slideshow.scrollWidth;
    const clientWidth = this.slideshow.clientWidth;
    const scrollLeft = this.slideshow.scrollLeft;

    // Full width available for scrollbar
    const availableWidth = clientWidth;

    // Calculate thumb width as percentage of visible area
    const thumbWidth = (clientWidth / scrollWidth) * availableWidth;

    // Calculate thumb position
    const thumbPosition = (scrollLeft / scrollWidth) * availableWidth;

    this.customThumb.style.width = `${thumbWidth}px`;
    this.customThumb.style.transform = `translateX(${thumbPosition}px)`;
  }

  /**
   * Handles mousedown on custom scrollbar thumb
   */
  handleThumbMouseDown(evt) {
    evt.preventDefault();
    this.isDraggingThumb = true;
    this.thumbDragStartX = evt.clientX;
    this.thumbDragStartScrollLeft = this.slideshow.scrollLeft;

    this.thumbMouseMoveHandler = this.handleThumbMouseMove.bind(this);
    this.thumbMouseUpHandler = this.handleThumbMouseUp.bind(this);

    document.addEventListener('mousemove', this.thumbMouseMoveHandler);
    document.addEventListener('mouseup', this.thumbMouseUpHandler);

    this.customThumb.style.cursor = 'grabbing';
  }

  /**
   * Handles mousemove when dragging scrollbar thumb
   */
  handleThumbMouseMove(evt) {
    if (!this.isDraggingThumb) return;
    evt.preventDefault();

    const deltaX = evt.clientX - this.thumbDragStartX;
    const scrollWidth = this.slideshow.scrollWidth;
    const clientWidth = this.slideshow.clientWidth;

    // Full width available for scrollbar
    const availableWidth = clientWidth;

    // Calculate scroll ratio
    const scrollRatio = deltaX / availableWidth;
    const scrollDelta = scrollRatio * scrollWidth;

    this.slideshow.scrollLeft = this.thumbDragStartScrollLeft + scrollDelta;
  }

  /**
   * Handles mouseup when dragging scrollbar thumb
   */
  handleThumbMouseUp() {
    this.isDraggingThumb = false;
    document.removeEventListener('mousemove', this.thumbMouseMoveHandler);
    document.removeEventListener('mouseup', this.thumbMouseUpHandler);
    this.customThumb.style.cursor = 'grab';
  }

  /**
   * Handles touchstart on custom scrollbar thumb
   */
  handleThumbTouchStart(evt) {
    evt.preventDefault();
    this.isDraggingThumb = true;
    const touch = evt.touches[0];
    this.thumbDragStartX = touch.clientX;
    this.thumbDragStartScrollLeft = this.slideshow.scrollLeft;

    this.thumbTouchMoveHandler = this.handleThumbTouchMove.bind(this);
    this.thumbTouchEndHandler = this.handleThumbTouchEnd.bind(this);

    document.addEventListener('touchmove', this.thumbTouchMoveHandler, { passive: false });
    document.addEventListener('touchend', this.thumbTouchEndHandler);
  }

  /**
   * Handles touchmove when dragging scrollbar thumb
   */
  handleThumbTouchMove(evt) {
    if (!this.isDraggingThumb) return;
    evt.preventDefault();

    const touch = evt.touches[0];
    const deltaX = touch.clientX - this.thumbDragStartX;
    const scrollWidth = this.slideshow.scrollWidth;
    const clientWidth = this.slideshow.clientWidth;

    // Full width available for scrollbar
    const availableWidth = clientWidth;

    // Calculate scroll ratio
    const scrollRatio = deltaX / availableWidth;
    const scrollDelta = scrollRatio * scrollWidth;

    this.slideshow.scrollLeft = this.thumbDragStartScrollLeft + scrollDelta;
  }

  /**
   * Handles touchend when dragging scrollbar thumb
   */
  handleThumbTouchEnd() {
    this.isDraggingThumb = false;
    document.removeEventListener('touchmove', this.thumbTouchMoveHandler);
    document.removeEventListener('touchend', this.thumbTouchEndHandler);
  }
}

customElements.define('visore-slide-show-scrollbar', VisoreSlideShowScrollbar);

/**
 * Custom scrollbar for Banner Slideshow on mobile devices
 * Extends the same functionality as VisoreSlideShowScrollbar but with different element name and thumb class
 */
class BannerSlideShowScrollbar extends HTMLElement {
  constructor() {
    super();
    this.slideshow = null;
    this.customThumb = null;
    this.isDraggingThumb = false;
  }

  connectedCallback() {
    // Only initialize on mobile
    if (window.innerWidth > 768) return;

    this.slideshow = this.querySelector('.slideshow');
    if (!this.slideshow) return;

    this.initCustomScrollbar();

    // Reinitialize on window resize
    this.resizeHandler = () => {
      if (window.innerWidth > 768) {
        this.destroyCustomScrollbar();
      } else if (!this.customThumb) {
        this.initCustomScrollbar();
      }
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    this.destroyCustomScrollbar();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * Initializes custom scrollbar for mobile devices
   */
  initCustomScrollbar() {
    if (this.customThumb || !this.slideshow) return;

    // Always create track element (Safari doesn't support pseudo-elements on custom elements)
    if (!this.querySelector('.banner-scrollbar-track')) {
      const track = document.createElement('div');
      track.className = 'banner-scrollbar-track';
      this.appendChild(track);
    }

    // Create custom scrollbar thumb element with banner-specific class
    this.customThumb = document.createElement('div');
    this.customThumb.className = 'banner-scrollbar-thumb';
    this.appendChild(this.customThumb);

    // Update scrollbar on scroll
    this.customScrollHandler = this.updateCustomScrollbar.bind(this);
    this.slideshow.addEventListener('scroll', this.customScrollHandler);

    // Add drag functionality to custom scrollbar
    this.thumbMouseDownHandler = this.handleThumbMouseDown.bind(this);
    this.thumbTouchStartHandler = this.handleThumbTouchStart.bind(this);

    this.customThumb.addEventListener('mousedown', this.thumbMouseDownHandler);
    this.customThumb.addEventListener('touchstart', this.thumbTouchStartHandler, { passive: false });

    // Initial update
    this.updateCustomScrollbar();
  }

  /**
   * Destroys custom scrollbar
   */
  destroyCustomScrollbar() {
    if (this.customThumb) {
      this.customThumb.remove();
      this.customThumb = null;
    }
    if (this.customScrollHandler && this.slideshow) {
      this.slideshow.removeEventListener('scroll', this.customScrollHandler);
    }
  }

  /**
   * Updates the position and width of custom scrollbar thumb
   */
  updateCustomScrollbar() {
    if (!this.customThumb || !this.slideshow) return;

    const scrollWidth = this.slideshow.scrollWidth;
    const clientWidth = this.slideshow.clientWidth;
    const scrollLeft = this.slideshow.scrollLeft;

    // Full width available for scrollbar
    const availableWidth = clientWidth;

    // Calculate thumb width as percentage of visible area
    const thumbWidth = (clientWidth / scrollWidth) * availableWidth;

    // Calculate thumb position
    const thumbPosition = (scrollLeft / scrollWidth) * availableWidth;

    this.customThumb.style.width = `${thumbWidth}px`;
    this.customThumb.style.transform = `translateX(${thumbPosition}px)`;
  }

  /**
   * Handles mousedown on custom scrollbar thumb
   */
  handleThumbMouseDown(evt) {
    evt.preventDefault();
    this.isDraggingThumb = true;
    this.thumbDragStartX = evt.clientX;
    this.thumbDragStartScrollLeft = this.slideshow.scrollLeft;

    this.thumbMouseMoveHandler = this.handleThumbMouseMove.bind(this);
    this.thumbMouseUpHandler = this.handleThumbMouseUp.bind(this);

    document.addEventListener('mousemove', this.thumbMouseMoveHandler);
    document.addEventListener('mouseup', this.thumbMouseUpHandler);

    this.customThumb.style.cursor = 'grabbing';
  }

  /**
   * Handles mousemove when dragging scrollbar thumb
   */
  handleThumbMouseMove(evt) {
    if (!this.isDraggingThumb) return;
    evt.preventDefault();

    const deltaX = evt.clientX - this.thumbDragStartX;
    const scrollWidth = this.slideshow.scrollWidth;
    const clientWidth = this.slideshow.clientWidth;

    // Full width available for scrollbar
    const availableWidth = clientWidth;

    // Calculate scroll ratio
    const scrollRatio = deltaX / availableWidth;
    const scrollDelta = scrollRatio * scrollWidth;

    this.slideshow.scrollLeft = this.thumbDragStartScrollLeft + scrollDelta;
  }

  /**
   * Handles mouseup when dragging scrollbar thumb
   */
  handleThumbMouseUp() {
    this.isDraggingThumb = false;
    document.removeEventListener('mousemove', this.thumbMouseMoveHandler);
    document.removeEventListener('mouseup', this.thumbMouseUpHandler);
    this.customThumb.style.cursor = 'grab';
  }

  /**
   * Handles touchstart on custom scrollbar thumb
   */
  handleThumbTouchStart(evt) {
    evt.preventDefault();
    this.isDraggingThumb = true;
    const touch = evt.touches[0];
    this.thumbDragStartX = touch.clientX;
    this.thumbDragStartScrollLeft = this.slideshow.scrollLeft;

    this.thumbTouchMoveHandler = this.handleThumbTouchMove.bind(this);
    this.thumbTouchEndHandler = this.handleThumbTouchEnd.bind(this);

    document.addEventListener('touchmove', this.thumbTouchMoveHandler, { passive: false });
    document.addEventListener('touchend', this.thumbTouchEndHandler);
  }

  /**
   * Handles touchmove when dragging scrollbar thumb
   */
  handleThumbTouchMove(evt) {
    if (!this.isDraggingThumb) return;
    evt.preventDefault();

    const touch = evt.touches[0];
    const deltaX = touch.clientX - this.thumbDragStartX;
    const scrollWidth = this.slideshow.scrollWidth;
    const clientWidth = this.slideshow.clientWidth;

    // Full width available for scrollbar
    const availableWidth = clientWidth;

    // Calculate scroll ratio
    const scrollRatio = deltaX / availableWidth;
    const scrollDelta = scrollRatio * scrollWidth;

    this.slideshow.scrollLeft = this.thumbDragStartScrollLeft + scrollDelta;
  }

  /**
   * Handles touchend when dragging scrollbar thumb
   */
  handleThumbTouchEnd() {
    this.isDraggingThumb = false;
    document.removeEventListener('touchmove', this.thumbTouchMoveHandler);
    document.removeEventListener('touchend', this.thumbTouchEndHandler);
  }
}

customElements.define('banner-slide-show-scrollbar', BannerSlideShowScrollbar);
